import React, { useCallback, useMemo } from 'react';
import { Wrapper } from './styles';
import AttachmentFile from '@/components/AttachmentFile';
import Mention from './Mention';
import CommentFiles from '../CommentFiles';
import ApprovalAttachmentList, {
  ApprovalAttachmentItemData,
} from '../ApprovalAttachment';
import {
  useHtmlToReact,
  type ElementTransform,
  type NodeTransformContext,
} from '@/components/Common/HtmlToReact';
import { useImageZoomTrigger } from '@/components/Approval/ApprovalView/ImageZoomModal/context';

export type ContentWithMentionsProps = {
  // 코멘트 내용 전체 텍스트
  readonly content: string;
  // mention 표시 할 때, text 처럼 표시해야할 경우가 있음. 이를 판단하는 함수
  readonly checkIsText?: (id: string) => boolean;
  // 전자결재 코멘트면 멘션 텍스트에 팀명(departmentName)을 항상 포함.
  readonly isApproval?: boolean;
};

const normalizeMentionContent = (content: string): string =>
  content.replace(
    /data-lexical-beautiful-mention-data="(\{[^>]+?\})"/g,
    (_match, jsonStr) => {
      const escapedJson = jsonStr.replace(/"/g, '&quot;');
      return `data-lexical-beautiful-mention-data="${escapedJson}"`;
    },
  );

const getFileSizeFromElement = (element: Element): number | undefined => {
  const rawFileSize = element.getAttribute('data-file-size');
  if (rawFileSize == null || rawFileSize === '') return undefined;

  const fileSize = Number(rawFileSize);
  return Number.isFinite(fileSize) ? fileSize : undefined;
};

const ContentWithMentions = ({
  content,
  checkIsText,
  isApproval = false,
}: ContentWithMentionsProps) => {
  // Approval zoom Provider 하위면 trigger, 그 외(게시판 등)면 null.
  const onImageClick = useImageZoomTrigger();

  // 코멘트 도메인 transform — 멘션/첨부리스트/단일첨부/img onClick 1:1 매핑 (스타일/레이아웃 변형은 코어에서 처리)
  const transform = useCallback<ElementTransform>(
    (ctx: NodeTransformContext) => {
      const { element, tagName, index, props } = ctx;

      // 멘션 마크업 — 2가지 패턴 지원
      // 1) data-lexical-beautiful-mention(일반+전자결재 공통),
      // 2) data-lexical-* 없는 .mention-name 레거시 fallback. isApproval 은 분기 아닌 팀명 부착에만 관여.
      if (
        tagName === 'span' &&
        element.getAttribute('data-lexical-beautiful-mention') === 'true'
      ) {
        const dataAttr = element.getAttribute(
          'data-lexical-beautiful-mention-data',
        );
        if (dataAttr) {
          try {
            const decodedJson = dataAttr.replace(/&quot;/g, '"');
            const parsed = JSON.parse(decodedJson);
            const { id, name, departmentName } = parsed;
            // 팀명 suffix 는 전자결재만. 그 외는 textContent 그대로.
            let mentionText: string;
            if (isApproval) {
              const baseText = element.textContent || (name ? `@${name}` : '');
              const teamSuffix = departmentName ? `(${departmentName})` : '';
              mentionText =
                teamSuffix && !baseText.includes(teamSuffix)
                  ? `${baseText}${teamSuffix}`
                  : baseText;
            } else {
              mentionText = element.textContent || '';
            }

            return (
              <Mention
                key={`mention-${id}-${index}`}
                id={id}
                isText={checkIsText?.(id) ?? false}
              >
                {mentionText}
              </Mention>
            );
          } catch (e) {
            // 파싱 실패 시 원본 텍스트 반환
            console.error('❌ Failed to parse mention data:', {
              dataAttr,
              error: e,
              text: element.textContent,
            });
            return element.textContent;
          }
        }
      }

      if (
        tagName === 'span' &&
        element.classList.contains('mention-name') &&
        element.getAttribute('data-id')
      ) {
        const passportId = element.getAttribute('data-id') || '';
        const mentionText = element.textContent || '';

        return (
          <Mention
            key={`mention-v1-${passportId}-${index}`}
            id={passportId}
            isText={checkIsText?.(passportId) ?? false}
          >
            {mentionText}
          </Mention>
        );
      }

      // 첨부파일 리스트 div 처리
      if (
        tagName === 'div' &&
        element.getAttribute('data-file-list') === 'true'
      ) {
        // 품의(approval) 변형: 컨테이너 또는 자식 a 에 data-variant="approval"
        const isApproval =
          element.getAttribute('data-variant') === 'approval' ||
          Array.from(element.childNodes).some(
            (child) =>
              child.nodeType === Node.ELEMENT_NODE &&
              (child as Element).getAttribute('data-variant') === 'approval',
          );

        if (isApproval) {
          const items: ApprovalAttachmentItemData[] = Array.from(
            element.childNodes,
          )
            .filter(
              (child) =>
                child.nodeType === Node.ELEMENT_NODE &&
                (child as Element).tagName.toLowerCase() === 'a' &&
                (child as Element).getAttribute('data-name'),
            )
            .map((child) => {
              const el = child as Element;
              return {
                name: el.getAttribute('data-name') || '',
                link: el.getAttribute('data-link') || '',
                fileUuid: el.getAttribute('data-file-uuid') || undefined,
                status:
                  (el.getAttribute('data-status') as
                    | 'success'
                    | 'loading'
                    | 'error') || 'success',
                fileSize: getFileSizeFromElement(el),
                createdAt: el.getAttribute('data-created-at') || undefined,
              };
            });

          return (
            <ApprovalAttachmentList
              key={`approval-file-list-${index}`}
              items={items}
            />
          );
        }

        const fileChildren = Array.from(element.childNodes)
          .map((child, i) => {
            if (child.nodeType === Node.ELEMENT_NODE) {
              const el = child as Element;
              if (
                el.tagName.toLowerCase() === 'a' &&
                el.getAttribute('data-name')
              ) {
                const name = el.getAttribute('data-name') || '';
                const link = el.getAttribute('data-link') || '';
                const fileUuid =
                  el.getAttribute('data-file-uuid') || undefined;
                const fileSize = getFileSizeFromElement(el);
                const createdAt =
                  el.getAttribute('data-created-at') || undefined;
                const status =
                  (el.getAttribute('data-status') as
                    | 'success'
                    | 'loading'
                    | 'error') || 'success';

                return (
                  <AttachmentFile
                    key={`attachment-${link}-${i}`}
                    status={status}
                    file={{ name, link, fileUuid, fileSize, createdAt }}
                    isDeletable={false}
                    isPreviewable={true}
                  />
                );
              }
            }
            return null;
          })
          .filter(Boolean);

        return (
          <CommentFiles key={`file-list-${index}`}>{fileChildren}</CommentFiles>
        );
      }

      // 첨부파일 링크 처리 (단일 파일)
      if (tagName === 'a' && element.getAttribute('data-name')) {
        const name = element.getAttribute('data-name') || '';
        const link = element.getAttribute('data-link') || '';
        const fileUuid = element.getAttribute('data-file-uuid') || undefined;
        const fileSize = getFileSizeFromElement(element);
        const createdAt = element.getAttribute('data-created-at') || undefined;
        const status =
          (element.getAttribute('data-status') as
            | 'success'
            | 'loading'
            | 'error') || 'success';

        // 품의(approval) 변형: data-variant="approval"
        if (element.getAttribute('data-variant') === 'approval') {
          return (
            <ApprovalAttachmentList
              key={`approval-attachment-${link}-${index}`}
              items={[{ name, link, status, fileUuid, fileSize, createdAt }]}
            />
          );
        }

        return (
          <AttachmentFile
            key={`attachment-${link}-${index}`}
            status={status}
            file={{ name, link, fileUuid, fileSize, createdAt }}
            isDeletable={false}
            isPreviewable={true}
          />
        );
      }

      // 이미지 크게보기: Approval Provider 하위(onImageClick non-null)일 때만 onClick + cursor:pointer 주입.
      if (tagName === 'img' && onImageClick) {
        const imgSrc = element.getAttribute('src') || '';
        if (imgSrc) {
          props.onClick = () => onImageClick(imgSrc);
          props.style = {
            ...((props.style as Record<string, unknown>) ?? {}),
            cursor: 'pointer',
          };
        }
      }

      return undefined;
    },
    [checkIsText, onImageClick, isApproval],
  );

  const rendered = useHtmlToReact(content, {
    preprocess: normalizeMentionContent,
    transform,
  });

  return useMemo(() => <Wrapper>{rendered}</Wrapper>, [rendered]);
};

export default ContentWithMentions;
